import React, { useState, useEffect } from 'react';
import { useSite } from '../../context/SiteContext';
import { CorporateServiceItem } from '../../types';
import { EmptySiteNotice } from '../common/EmptySiteNotice';
import { AlertDialog } from '../common/AlertDialog';
import { DeleteService } from '../../services/deleteService';
import {
  Briefcase,
  Plus,
  Trash2,
  Edit2,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  DollarSign,
  Star,
  Search,
  Image as ImageIcon,
  Save,
  X,
  Layers,
} from 'lucide-react';

const DEFAULT_SERVICES: CorporateServiceItem[] = [
  {
    id: 'srv-1',
    title: 'Halı Yıkama & Dezenfeksiyon',
    slug: 'hali-yikama',
    shortDescription: 'Özel anti-bakteriyel şampuanlar ve tam otomatik rulo makinelerle derinlemesine halı yıkama.',
    detailedDescription: 'Yün, ipek, nepal, el dokuma ve akrilik halılarınız adresinizden ücretsiz servisimizle teslim alınır. 12 fırçalı tam otomatik makinelerimizde anti-bakteriyel organik deterjanlarla yıkanıp, santrifüj sıkma makinelerinde %95 oranında kurutulur. Kapalı tozsuz kurutma odalarında havalandırıldıktan sonra parfümlenip poşetlenerek adresinize teslim edilir.',
    coverImage: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=800&auto=format&fit=crop',
    price: '₺120 / m²',
    isFeatured: true,
    seoTitle: 'Dalaman Halı Yıkama — Profesyonel ve Hızlı Servis',
    seoDescription: 'Dalaman, Ortaca ve Göcek genelinde ücretsiz servis imkanıyla en kaliteli halı yıkama hizmeti.',
    icon: 'Briefcase',
  },
  {
    id: 'srv-2',
    title: 'Koltuk & Yatak Yıkama',
    slug: 'koltuk-yikama',
    shortDescription: 'Yerinde buharlı vakumlu derinlemesine koltuk ve yatak hijyen temizliği.',
    detailedDescription: 'Ev ve iş yerlerinizdeki koltuk takımları, köşe takımları ve yataklar yüksek emiş gücüne sahip sıcak buharlı vakum makineleri ile kumaş liflerine zarar vermeden yıkanır. Leke çıkarıcı özel enzimler uygulanarak inatçı kirler yok edilir.',
    coverImage: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?q=80&w=800&auto=format&fit=crop',
    price: '₺1.500 / Takım',
    isFeatured: true,
    seoTitle: 'Yerinde Koltuk Yıkama Hizmeti — Hijyenik Temizlik',
    seoDescription: 'Buharlı derin temizlik ile koltuklarınızdaki tüm mayt ve bakterilere son verin.',
    icon: 'Sparkles',
  },
  {
    id: 'srv-3',
    title: 'Yorgan & Battaniye Yıkama',
    slug: 'yorgan-yikama',
    shortDescription: 'Sanayi tipi geniş tamburlu makinelerde tek tek hijyenik yıkama.',
    detailedDescription: 'Elyaf, yün, pamuk ve kaz tüyü yorganlarınız kişiye özel makinelerde tek tek yıkanır. Çift durulama ve özel yumuşatıcılar ile kabartılarak kurutulur.',
    coverImage: 'https://images.unsplash.com/photo-1521783988139-89397d761dce?q=80&w=800&auto=format&fit=crop',
    price: '₺250 / Adet',
    isFeatured: false,
    seoTitle: 'Yorgan ve Battaniye Temizleme Servisi',
    seoDescription: 'Bireysel makinelerde hijyenik yorgan ve battaniye yıkama hizmeti.',
    icon: 'Layers',
  },
  {
    id: 'srv-4',
    title: 'Stor & Zebra Perde Yıkama',
    slug: 'stor-perde-yikama',
    shortDescription: 'Mekanizmalarına zarar verilmeden uzman ekipçe söküm, yıkama ve montaj.',
    detailedDescription: 'Ev ve ofislerinizdeki stor ve zebra perdeleriniz ekibimiz tarafından özenle sökülür, kumaş kaplamasını yıpratmayan özel fırça ve solüsyonlarla temizlenip tekrar monte edilir.',
    coverImage: 'https://images.unsplash.com/photo-1513694203232-719a280e022f?q=80&w=800&auto=format&fit=crop',
    price: '₺80 / m²',
    isFeatured: false,
    seoTitle: 'Stor ve Zebra Perde Yıkama — Ücretsiz Montaj',
    seoDescription: 'Zahmetsiz perde yıkama; sökme, temizleme ve takma işlemi tarafımızca yapılır.',
    icon: 'CheckCircle2',
  },
];

export const ServicesManager: React.FC<{ onNavigate?: (view: string) => void }> = ({ onNavigate }) => {
  const { activeSite, updateSiteData } = useSite();

  const [serviceList, setServiceList] = useState<CorporateServiceItem[]>(() => {
    return activeSite?.settings?.servicesList ?? DEFAULT_SERVICES;
  });
  const [editingItem, setEditingItem] = useState<CorporateServiceItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saveToast, setSaveToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    serviceId: string;
    serviceTitle: string;
    loading: boolean;
  }>({
    open: false,
    serviceId: '',
    serviceTitle: '',
    loading: false,
  });

  // Sync state when active site changes
  useEffect(() => {
    if (activeSite?.settings?.servicesList !== undefined) {
      setServiceList(activeSite.settings.servicesList);
    }
  }, [activeSite?.settings?.servicesList]);

  if (!activeSite) {
    return (
      <EmptySiteNotice
        title="Hizmet Yönetimi İçin Aktif Web Sitesi Gereklidir"
        description="Sunduğunuz kurumsal hizmetleri, fiyatlandırmaları ve açıklamaları ekleyip düzenlemek için lütfen bir web sitesi oluşturun veya mevcut sitelerinizden birini seçin."
        onOpenCreateSite={() => onNavigate && onNavigate('sites')}
        onReturnToDashboard={() => onNavigate && onNavigate('dashboard')}
      />
    );
  }

  const handleOpenAdd = () => {
    const newItem: CorporateServiceItem = {
      id: `srv-${Date.now()}`,
      title: '',
      slug: '',
      shortDescription: '',
      detailedDescription: '',
      coverImage: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=800&auto=format&fit=crop',
      price: '',
      isFeatured: false,
      seoTitle: '',
      seoDescription: '',
      icon: 'Briefcase',
    };
    setEditingItem(newItem);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (item: CorporateServiceItem) => {
    setEditingItem({ ...item });
    setIsModalOpen(true);
  };

  const handleDelete = (id: string, title?: string) => {
    setDeleteDialog({
      open: true,
      serviceId: id,
      serviceTitle: title || 'Bu hizmet',
      loading: false,
    });
  };

  const handleExecuteDelete = async () => {
    if (!activeSite || !deleteDialog.serviceId || deleteDialog.loading) return;
    setDeleteDialog((prev) => ({ ...prev, loading: true }));
    try {
      const res = await DeleteService.deleteService(activeSite.id, deleteDialog.serviceId);
      if (res.success) {
        const updated = serviceList.filter((s) => s.id !== deleteDialog.serviceId);
        setServiceList(updated);
        setDeleteDialog({ open: false, serviceId: '', serviceTitle: '', loading: false });
      } else {
        setErrorMessage(res.error || 'Hizmet silinemedi.');
        setDeleteDialog((prev) => ({ ...prev, loading: false }));
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Silme işlemi sırasında hata oluştu.');
      setDeleteDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleSaveModal = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem || !editingItem.title.trim()) return;

    // Generate slug if empty
    const slug = (editingItem.slug || '').trim() || (editingItem.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const finalItem: CorporateServiceItem = {
      ...editingItem,
      slug,
      seoTitle: editingItem.seoTitle || `${editingItem.title} — ${activeSite.name}`,
      seoDescription: editingItem.seoDescription || editingItem.shortDescription,
    };

    let updated: CorporateServiceItem[];
    const exists = serviceList.some((s) => s.id === finalItem.id);
    if (exists) {
      updated = serviceList.map((s) => (s.id === finalItem.id ? finalItem : s));
    } else {
      updated = [...serviceList, finalItem];
    }

    setServiceList(updated);
    setIsModalOpen(false);
    setEditingItem(null);
    saveServicesToSite(updated);
  };

  const saveServicesToSite = (items: CorporateServiceItem[]) => {
    // Also update any SERVICES sections across active site pages so they immediately reflect the real services
    const updatedPages = (activeSite.pages || []).map((p) => {
      const updatedSections = (p?.sections || []).map((sec) => {
        if (sec.type === 'SERVICES') {
          return {
            ...sec,
            content: {
              ...sec.content,
              items: items.map((itm) => ({
                id: itm.id,
                title: itm.title,
                description: itm.shortDescription,
                price: itm.price,
                imageUrl: itm.coverImage,
                link: `/hizmet/${itm.slug}`,
                icon: itm.icon || 'Briefcase',
              })),
            },
          };
        }
        return sec;
      });
      return { ...p, sections: updatedSections };
    });

    updateSiteData({
      pages: updatedPages,
      settings: {
        ...(activeSite?.settings || {}),
        servicesList: items,
      },
    });

    setSaveToast(true);
    setTimeout(() => setSaveToast(false), 3000);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-6 animate-in fade-in select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2 text-indigo-600">
            <Briefcase className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-wider">Hizmet ve Çözüm Yönetimi</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
            Hizmetler ({serviceList.length})
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 max-w-2xl">
            İşletmenizin sunduğu tüm hizmetleri buradan ekleyin ve yönetin. Web sitenizdeki tüm Hizmetler blokları bu gerçek verilerle anında güncellenir.
          </p>
        </div>

        <button
          type="button"
          onClick={handleOpenAdd}
          className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs shadow-lg shadow-indigo-500/25 transition flex items-center gap-2 self-start sm:self-auto"
        >
          <Plus className="w-4 h-4" />
          <span>Yeni Hizmet Ekle</span>
        </button>
      </div>

      {saveToast && (
        <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold flex items-center gap-2 animate-in fade-in">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>Hizmetler güncellendi ve web sitesi bileşenlerine otomatik yansıtıldı.</span>
        </div>
      )}

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {serviceList.map((service) => (
          <div
            key={service.id}
            className="p-5 rounded-2xl bg-white border border-slate-200/80 shadow-xs hover:shadow-md transition flex flex-col justify-between space-y-4 group"
          >
            <div className="space-y-3">
              {/* Cover Image */}
              {service.coverImage && (
                <div className="w-full h-40 rounded-xl overflow-hidden bg-slate-100 relative">
                  <img
                    src={service.coverImage}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {service.isFeatured && (
                    <span className="absolute top-2 left-2 px-2.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center gap-1 shadow-md">
                      <Star className="w-3 h-3 fill-current" /> Öne Çıkan
                    </span>
                  )}
                  {service.price && (
                    <span className="absolute bottom-2 right-2 px-2.5 py-1 rounded-lg bg-slate-950/80 text-white text-[11px] font-bold backdrop-blur-xs">
                      {service.price}
                    </span>
                  )}
                </div>
              )}

              <div>
                <h3 className="font-extrabold text-base text-slate-900 group-hover:text-indigo-600 transition">
                  {service.title}
                </h3>
                <div className="text-[11px] font-mono text-slate-400 mt-0.5">/hizmet/{service.slug}</div>
              </div>

              <p className="text-xs text-slate-500 line-clamp-3 leading-relaxed">
                {service.shortDescription}
              </p>
            </div>

            {/* Actions */}
            <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
              <span className="text-[11px] font-medium text-slate-400">
                {service.detailedDescription ? 'Detaylı Sayfa Var' : 'Yalnızca Kart'}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleOpenEdit(service)}
                  className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition"
                  title="Düzenle"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(service.id, service.title)}
                  className="p-2 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 transition"
                  title="Sil"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add / Edit Modal */}
      {isModalOpen && editingItem && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 space-y-5 shadow-2xl border border-slate-200 my-8">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-900">
                {editingItem.title ? 'Hizmeti Düzenle' : 'Yeni Hizmet Oluştur'}
              </h2>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveModal} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1 sm:col-span-2">
                  <label className="text-xs font-bold text-slate-700">Hizmet Başlığı *</label>
                  <input
                    type="text"
                    value={editingItem?.title || ''}
                    onChange={(e) => editingItem && setEditingItem({ ...editingItem, title: e.target.value })}
                    placeholder="Örn: Profesyonel Halı Yıkama"
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-sm font-semibold text-slate-900 focus:bg-white focus:border-indigo-500 outline-hidden"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700">URL Slug</label>
                  <input
                    type="text"
                    value={editingItem?.slug || ''}
                    onChange={(e) => editingItem && setEditingItem({ ...editingItem, slug: e.target.value })}
                    placeholder="hali-yikama"
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs font-mono text-slate-900 focus:bg-white focus:border-indigo-500 outline-hidden"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700">Fiyat Bilgisi (Opsiyonel)</label>
                  <input
                    type="text"
                    value={editingItem?.price || ''}
                    onChange={(e) => editingItem && setEditingItem({ ...editingItem, price: e.target.value })}
                    placeholder="Örn: ₺120 / m² veya Teklif İsteyin"
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs text-slate-900 focus:bg-white focus:border-indigo-500 outline-hidden"
                  />
                </div>

                <div className="space-y-1 sm:col-span-2">
                  <label className="text-xs font-bold text-slate-700">Kısa Açıklama (Kartlar için) *</label>
                  <textarea
                    rows={2}
                    value={editingItem?.shortDescription || ''}
                    onChange={(e) => editingItem && setEditingItem({ ...editingItem, shortDescription: e.target.value })}
                    placeholder="Ana sayfada hizmet kartında görünecek 1-2 cümlelik açıklama..."
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs text-slate-900 focus:bg-white focus:border-indigo-500 outline-hidden"
                    required
                  />
                </div>

                <div className="space-y-1 sm:col-span-2">
                  <label className="text-xs font-bold text-slate-700">Detaylı Açıklama (Hizmet Detay Sayfası)</label>
                  <textarea
                    rows={4}
                    value={editingItem?.detailedDescription || ''}
                    onChange={(e) => editingItem && setEditingItem({ ...editingItem, detailedDescription: e.target.value })}
                    placeholder="Hizmet sürecini, kullanılan teknolojileri ve avantajlarını anlatan detaylı metin..."
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs text-slate-900 focus:bg-white focus:border-indigo-500 outline-hidden"
                  />
                </div>

                <div className="space-y-1 sm:col-span-2">
                  <label className="text-xs font-bold text-slate-700">Kapak Görseli URL</label>
                  <input
                    type="text"
                    value={editingItem?.coverImage || ''}
                    onChange={(e) => editingItem && setEditingItem({ ...editingItem, coverImage: e.target.value })}
                    placeholder="https://images.unsplash.com/..."
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs font-mono text-slate-900 focus:bg-white focus:border-indigo-500 outline-hidden"
                  />
                </div>

                <div className="sm:col-span-2 flex items-center gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="isFeatured"
                    checked={editingItem?.isFeatured || false}
                    onChange={(e) => editingItem && setEditingItem({ ...editingItem, isFeatured: e.target.checked })}
                    className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300"
                  />
                  <label htmlFor="isFeatured" className="text-xs font-bold text-slate-700 cursor-pointer">
                    Ana sayfada Öne Çıkan Hizmetler arasında rozetle göster
                  </label>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold transition shadow-md shadow-indigo-500/20"
                >
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation AlertDialog */}
      <AlertDialog
        open={deleteDialog.open}
        title="Hizmeti Silmek İstediğinize Emin Misiniz?"
        description={`"${deleteDialog.serviceTitle}" hizmeti kalıcı olarak silinecektir. Bu işlem geri alınamaz.`}
        variant="destructive"
        confirmText={deleteDialog.loading ? 'Siliniyor...' : 'Evet, Sil'}
        cancelText="Vazgeç"
        loading={deleteDialog.loading}
        onConfirm={handleExecuteDelete}
        onCancel={() => {
          if (!deleteDialog.loading) {
            setDeleteDialog({ open: false, serviceId: '', serviceTitle: '', loading: false });
          }
        }}
      />

      {/* Error Notice AlertDialog */}
      <AlertDialog
        open={Boolean(errorMessage)}
        title="İşlem Başarısız"
        description={errorMessage || ''}
        variant="warning"
        singleButton
        confirmText="Tamam"
        onCancel={() => setErrorMessage(null)}
      />
    </div>
  );
};
